import { Injectable } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, startWith, Subject, take } from "rxjs";
import { IRequest } from "ngxs-requests-plugin";

import {
  ClearSearchedSubTreeVariable,
  ClearSearchedVariable,
  ClearVariableInfo,
  ClearVariableValueSearchResults,
  GetVariableInfo,
  GetVariablesPreset,
  GetVariablesPresetList,
  GetVariablesTree as GetVariableTree,
  SearchSubTreeVariable,
  SearchVariable,
  SearchVariableValue,
} from '../ngxs/variables/variables.actions';
import {
  GetVariableInfoRequestState,
  GetVariablesPresetListRequestState,
  GetVariablesPresetRequestState,
  GetVariablesTreeRequestState,
  SearchSubTreeVariableRequestState,
  SearchVariableRequestState,
  SearchVariableValueRequestState,
  VariablesState,
} from '../ngxs/variables/variables.state';
import { VariablesGetterState } from "../ngxs/variables/variables-getter.state";
import { ISearchContentItem, ISearchSubTreeContentItem, IVariable } from "../interfaces/variable.interface";
import { SelectedVariablesGetterState } from "../ngxs/selected-variables/selected-variables-getter.state";
import { AddSelectedVariables, RemoveSelectedVariables, SaveVariablesPreset } from "../ngxs/selected-variables/selected-variables.actions";
import { GetVariableSubtree, SearchSubtreeVariable } from "../ngxs/variables-subtrees/variables-subtrees.actions";
import { VariablesSubtreesGetterState } from "../ngxs/variables-subtrees/variables-subtrees-getter.state";
import { GetVariableSubtreeRequestState, SearchSubtreeVariableRequestState } from "../ngxs/variables-subtrees/variables-subtrees.state";
import { AddSelectedSubtreeVariables, RemoveSelectedSubtreeVariables, ResetSelectedSubtreeVariables } from "../ngxs/selected-subtree-variables/selected-subtree-variables.actions";
import { SelectedSubtreeVariablesGetterState } from "../ngxs/selected-subtree-variables/selected-subtree-variables-getter.state";
import { SaveVariablesPresetRequestState, SelectedVariablesState } from "../ngxs/selected-variables/selected-variables.state";
import { StateReset } from "ngxs-reset-plugin";
import { IPresetEntity, IPresetReduced } from "../interfaces/presets.interface";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
  providedIn: 'root'
})
export class VariablesService {
  removeSelectedVariableSubject$: Subject<string> = new Subject<string>();
  clearAllSelectedVariablesSubject$: Subject<void> = new Subject<void>();

  MAX_VARIABLES_SELECTION = 150;

  @Select(GetVariablesTreeRequestState)
  getVariablesTreeRequestState$: Observable<IRequest>;

  @Select(GetVariableInfoRequestState)
  getVariableInfoRequestState$: Observable<IRequest>;

  @Select(GetVariableSubtreeRequestState)
  getVariableSubtreeRequestState$: Observable<IRequest>;

  @Select(GetVariablesPresetListRequestState)
  getVariablesPresetListRequestState$: Observable<IRequest>;

  @Select(GetVariablesPresetRequestState)
  getVariablesPresetRequestState$: Observable<IRequest>;

  @Select(SaveVariablesPresetRequestState)
  saveVariablesPresetRequestState$: Observable<IRequest>;

  @Select(SearchVariableRequestState)
  searchVariableRequestState$: Observable<IRequest>;

  @Select(SearchVariableValueRequestState)
  searchVariableValueRequestState$: Observable<IRequest>;

  @Select(SearchSubTreeVariableRequestState)
  searchSubTreeVariableRequestState$: Observable<IRequest>;

  @Select(SearchSubtreeVariableRequestState)
  searchSubtreeVariableRequestState$: Observable<IRequest>;

  @Select(VariablesGetterState.getVariablesTree)
  variablesTree$: Observable<IVariable[]>;

  @Select(VariablesGetterState.getTagInfo)
  tagInfo$: Observable<{[key: string]: {label: string}}>;

  @Select(VariablesGetterState.getSearchResults)
  variableSearchResults$: Observable<ISearchContentItem[]>;

  @Select(VariablesGetterState.getSearchedVariableSubTree)
  searchedVariableSubTree$: Observable<ISearchSubTreeContentItem[]>;

  @Select(VariablesSubtreesGetterState.getVariableSubtree)
  variableSubTree$: Observable<IVariable>;

  @Select(VariablesGetterState.getVariableInstruction)
  variableInstruction$: Observable<string>;

  @Select(VariablesGetterState.getFlatVariables)
  flatVariables$: Observable<{[key: string]: IVariable}[]>;

  @Select(SelectedVariablesGetterState.getSelectedVariables)
  selectedVariables$: Observable<string[]>;

  @Select(SelectedSubtreeVariablesGetterState.getSelectedSubTreeVariables)
  selectedSubTreeVariables$: Observable<IVariable[]>;

  @Select(SelectedSubtreeVariablesGetterState.getSelectedSubTreeVariableValues)
  selectedSubTreeVariableValues$: Observable<string[]>;

  @Select(VariablesGetterState.getVariableValuesSearchResults)
  variableValuesSearchResults$: Observable<{label: string, value: string}[]>;

  @Select(VariablesGetterState.getVariablesPresetList)
  variablesPresetList$: Observable<IPresetReduced[]>;

  @Select(VariablesGetterState.getVariablesPreset)
  variablesPreset$: Observable<IPresetEntity>;

  unsavedVariableChanges$: Observable<boolean>;

  constructor(
    private store: Store,
    private localStorageService: LocalStorageService,
  ) {
    // Track unsaved variable changes by comparing in-memory selections with localStorage
    this.unsavedVariableChanges$ = combineLatest([
      this.selectedVariables$,
      this.localStorageService.selectedVariablesSubject$
    ]).pipe(
      map(([currentSelection, localStorageSelection]) => {
        const localStorageSet = new Set(localStorageSelection || []);
        const currentSet = new Set(currentSelection || []);
        if (localStorageSet.size !== currentSet.size) {
          return true;
        }
        for (let tag of currentSet) {
          if (!localStorageSet.has(tag)) {
            return true;
          }
        }
        return false;
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getVariableTree() {
    this.store.dispatch(new GetVariableTree());
  }

  clearVariableTree() {
    this.store.dispatch(new StateReset(VariablesState));
  }

  getVariableInfo(tag: string) {
    this.store.dispatch(new GetVariableInfo(tag));
  }

  clearVariableInfo() {
    this.store.dispatch(new ClearVariableInfo());
  }

  searchVariable(input: string) {
    this.store.dispatch(new SearchVariable(input));
  }

  searchVariableValues(variableName: string, input: string) {
    this.store.dispatch(new SearchVariableValue({variableName, input}));
  }

  clearSearchedVariable() {
    this.store.dispatch(new ClearSearchedVariable());
  }

  searchSubTreeVariable(filterTreeName: string, query: string) {
    this.store.dispatch(new SearchSubTreeVariable({filterTreeName, query}));
  }

  getSearchedVariableSubTree() {
    return this.store.select(VariablesGetterState.getSearchedVariableSubTree);
  }

  clearSearchedVariableSubTree() {
    this.store.dispatch(new ClearSearchedSubTreeVariable());
  }

  clearVariableValueSearchResults() {
    this.store.dispatch(new ClearVariableValueSearchResults());
  }

  addSelectedVariables(variables: string[], replace=false) {
    this.store.dispatch(new AddSelectedVariables(variables, replace));
  }

  removeSelectedVariables(variables: string[] | string[]) {
    this.store.dispatch(new RemoveSelectedVariables(variables));
  }

  getVariableSubtree(filterTreeName: string) {
    this.store.dispatch(new GetVariableSubtree(filterTreeName));
  }

  getFlatVariable(tag: string) {
    return this.store.select(VariablesGetterState.getFlatVariable(tag));
  }

  addSelectedSubtreeVariables(variables: string[]) {
    this.store.dispatch(new AddSelectedSubtreeVariables(variables));
  }

  removeSelectedSubtreeVariables(variables: string[]) {
    this.store.dispatch(new RemoveSelectedSubtreeVariables(variables));
  }

  resetSelectedSubtreeVariables() {
    this.store.dispatch(new ResetSelectedSubtreeVariables());
  }

  getSortedSelectedVariables$(sort: string) {
    return this.store.select(SelectedVariablesGetterState.getSortedSelectedVariables(sort));
  }

  getVariablesPresetList() {
    this.store.dispatch(new GetVariablesPresetList());
  }

  getVariablesPreset(presetId: string) {
    this.store.dispatch(new GetVariablesPreset(presetId));
  }

  saveVariablesPreset(name: string, variables: string[]) {
    this.store.dispatch(new SaveVariablesPreset(name, variables));
  }

  clearSelectedVariables() {
    this.store.dispatch(new StateReset(SelectedVariablesState));
  }

  searchSubtreeVariable(filterTreeName: string, input: string) {
    this.store.dispatch(new SearchSubtreeVariable({filterTreeName, input}));
  }
}
